/**
 * ISO 11649 Creditor Reference (RF) utilities
 * 
 * Format: RF + 2 check digits + reference (up to 21 alphanumeric chars)
 * Check digits calculated using ISO 7064 Mod 97-10
 */

/**
 * Convert a character to its numeric value for MOD 97 calculation
 * A=10, B=11, ..., Z=35
 */
function charToNumber(char: string): string {
  const code = char.toUpperCase().charCodeAt(0)
  if (code >= 48 && code <= 57) {
    // 0-9
    return char
  } else if (code >= 65 && code <= 90) {
    // A-Z -> 10-35
    return (code - 55).toString()
  }
  return ''
}

/**
 * Calculate MOD 97 for a large number represented as string
 */
function mod97(numStr: string): number {
  let remainder = 0
  for (const digit of numStr) {
    remainder = (remainder * 10 + parseInt(digit, 10)) % 97
  }
  return remainder
}

/**
 * Calculate the check digits for an RF creditor reference
 * @param reference - The reference string (without RF prefix)
 * @returns Two-digit check string (00-96)
 */
export function calculateRfCheckDigits(reference: string): string {
  if (!reference) return '00'
  
  // Clean the reference: only alphanumeric, uppercase
  const cleaned = reference.replace(/[^A-Za-z0-9]/g, '').toUpperCase()
  if (!cleaned) return '00'
  
  // Convert reference to numeric string
  let numericRef = ''
  for (const char of cleaned) {
    numericRef += charToNumber(char)
  }
  
  // Append RF00 (R=27, F=15, 00)
  numericRef += '271500'
  
  // Calculate check digits: 98 - (numericRef mod 97)
  const checkDigits = 98 - mod97(numericRef)
  
  return checkDigits.toString().padStart(2, '0')
}

/**
 * Generate a complete RF creditor reference
 * @param reference - The user's reference (invoice number, etc.)
 * @returns Complete RF reference with check digits
 */
export function generateRfReference(reference: string): string {
  if (!reference) return ''
  
  const cleaned = reference.replace(/[^A-Za-z0-9]/g, '').toUpperCase()
  if (!cleaned) return ''
  
  // Max 21 characters for the reference part
  const truncated = cleaned.slice(0, 21)
  const checkDigits = calculateRfCheckDigits(truncated)
  
  return `RF${checkDigits}${truncated}`
}

/**
 * Validate an RF creditor reference
 * @param rfReference - Complete RF reference to validate
 * @returns Whether the reference is valid
 */
export function validateRfReference(rfReference: string): boolean {
  if (!rfReference) return false
  
  const cleaned = rfReference.replace(/[^A-Za-z0-9]/g, '').toUpperCase()
  
  // Must start with RF and have at least 5 chars (RF + 2 check + 1 ref)
  if (!cleaned.startsWith('RF') || cleaned.length < 5 || cleaned.length > 25) {
    return false
  }
  
  // Extract parts
  const checkDigits = cleaned.slice(2, 4)
  const reference = cleaned.slice(4)
  
  // Check digits must be numeric
  if (!/^\d{2}$/.test(checkDigits)) {
    return false
  }
  
  // Recalculate and compare
  const expectedCheckDigits = calculateRfCheckDigits(reference)
  return checkDigits === expectedCheckDigits
}

/**
 * Parse an RF reference into its components
 */
export function parseRfReference(rfReference: string): {
  isValid: boolean
  checkDigits: string
  reference: string
  formatted: string
} {
  const cleaned = rfReference.replace(/[^A-Za-z0-9]/g, '').toUpperCase()
  
  if (!cleaned.startsWith('RF') || cleaned.length < 5) {
    return { isValid: false, checkDigits: '', reference: '', formatted: '' }
  }
  
  const checkDigits = cleaned.slice(2, 4)
  const reference = cleaned.slice(4)
  const isValid = validateRfReference(cleaned)
  
  // Format with spaces every 4 characters
  const formatted = cleaned.replace(/(.{4})/g, '$1 ').trim()
  
  return { isValid, checkDigits, reference, formatted }
}

/**
 * Format a reference string for display (spaces every 4 chars)
 */
export function formatRfReference(rfReference: string): string {
  const cleaned = rfReference.replace(/[^A-Za-z0-9]/g, '').toUpperCase()
  return cleaned.replace(/(.{4})/g, '$1 ').trim()
}

